import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Drawer, Box, Typography, IconButton, TextField, InputAdornment, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Button, Checkbox, Chip, Alert, Stack, Autocomplete } from '@mui/material';
import { Search as SearchIcon, DeleteOutlined as DeleteIcon, FilterListOutlined as FilterIcon } from '@mui/icons-material';
import Popper from '@mui/material/Popper';
import { UserAPI } from '../apis/UserAPI';
// Custom Popper for Autocomplete to fix alignment
const FullWidthPopper = (props) => (_jsx(Popper, { ...props, style: {
        width: props.style?.width,
        minWidth: 300,
        ...props.style,
        left: 0
    }, placement: "bottom-start" }));
const UserGroupDrawer = ({ open, onClose, group, onDelete, onMembersUpdated }) => {
    const [members, setMembers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const [autocompleteLoading, setAutocompleteLoading] = useState(false);
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);
    useEffect(() => {
        if (group && open) {
            loadMembers();
            loadAllUsers();
        }
    }, [group, open]);
    const loadMembers = async () => {
        if (!group)
            return;
        setLoading(true);
        setError(null);
        try {
            const response = await UserAPI.getUserGroupMembers(group.id.toString());
            const transformedMembers = response.resource
                ? response.resource.map((member) => {
                    const firstName = member.first_name || '';
                    const lastName = member.last_name || '';
                    const name = member.userAttributes?.name || '';
                    const email = member.email || member.username || '';
                    let initials = '';
                    if (firstName && lastName) {
                        initials = `${firstName[0]}${lastName[0]}`;
                    }
                    else if (name) {
                        const nameParts = name.trim().split(/\s+/);
                        const firstInitial = nameParts[0]?.[0] ?? '';
                        const lastInitial = nameParts.at(-1)?.[0] ?? '';
                        initials =
                            nameParts.length > 1
                                ? `${firstInitial}${lastInitial}`
                                : name.substring(0, 2);
                        initials = initials.toUpperCase();
                    }
                    else if (email) {
                        initials = email.substring(0, 2).toUpperCase();
                    }
                    return {
                        id: member.id,
                        firstName: firstName,
                        lastName: lastName,
                        name: name,
                        email: email,
                        initials: initials
                    };
                })
                : [];
            setMembers(transformedMembers);
        }
        catch (err) {
            console.error('Failed to load members:', err);
            if (err?.response?.status === 404) {
                setMembers([]);
            }
            else {
                setError('Failed to load group members');
            }
        }
        finally {
            setLoading(false);
        }
    };
    const loadAllUsers = async () => {
        setAutocompleteLoading(true);
        try {
            const response = await UserAPI.getUsers();
            const users = response.resource || [];
            const transformedUsers = users.map((user) => {
                const firstName = user.first_name || user.firstName || '';
                const lastName = user.last_name || user.lastName || '';
                const name = user.userAttributes?.name || '';
                const email = user.email || user.username || '';
                let initials = '';
                if (firstName && lastName) {
                    initials = `${firstName[0]}${lastName[0]}`;
                }
                else if (name) {
                    const nameParts = name.trim().split(/\s+/);
                    const firstInitial = nameParts[0]?.[0] ?? '';
                    const lastInitial = nameParts.at(-1)?.[0] ?? '';
                    initials =
                        nameParts.length > 1
                            ? `${firstInitial}${lastInitial}`
                            : name.substring(0, 2);
                    initials = initials.toUpperCase();
                }
                else if (email) {
                    initials = email.substring(0, 2).toUpperCase();
                }
                return {
                    id: user.id,
                    firstName: firstName,
                    lastName: lastName,
                    name: name,
                    email: email,
                    initials: initials
                };
            });
            setAllUsers(transformedUsers);
        }
        catch (err) {
            console.error('Failed to load users:', err);
            setAllUsers([]);
        }
        finally {
            setAutocompleteLoading(false);
        }
    };
    const toggleMemberSelection = (memberId) => {
        setSelectedMemberIds((prev) => prev.includes(memberId)
            ? prev.filter((id) => id !== memberId)
            : [...prev, memberId]);
    };
    const handleAddMembers = async () => {
        if (!group || selectedUsers.length === 0)
            return;
        setLoading(true);
        setError(null);
        try {
            await UserAPI.addUsersToGroup(group.id.toString(), selectedUsers.map((u) => u.id));
            setSelectedUsers([]);
            await loadMembers();
            onMembersUpdated();
        }
        catch (err) {
            console.error('Failed to add members:', err);
            setError('Failed to add members to group');
        }
        finally {
            setLoading(false);
        }
    };
    const handleRemoveMember = async (userId) => {
        if (!group)
            return;
        setLoading(true);
        setError(null);
        try {
            await UserAPI.removeUsersFromGroup(group.id.toString(), [userId]);
            await loadMembers();
            onMembersUpdated();
        }
        catch (err) {
            console.error('Failed to remove member:', err);
            setError('Failed to remove member from group');
        }
        finally {
            setLoading(false);
        }
    };
    const handleBulkRemoveMembers = async () => {
        if (!group || selectedMemberIds.length === 0)
            return;
        setLoading(true);
        setError(null);
        try {
            await UserAPI.removeUsersFromGroup(group.id.toString(), selectedMemberIds);
            setSelectedMemberIds([]);
            await loadMembers();
            onMembersUpdated();
        }
        catch (err) {
            console.error('Failed to bulk remove members:', err);
            setError('Failed to remove selected members from group');
        }
        finally {
            setLoading(false);
        }
    };
    if (!group)
        return null;
    return (_jsx(Drawer, { anchor: "right", open: open, onClose: onClose, sx: {
            '& .MuiDrawer-paper': {
                width: 600,
                borderRadius: '24px 0 0 24px',
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)'
            }
        }, children: _jsxs(Box, { sx: {
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }, children: [_jsxs(Box, { sx: {
                        p: 2,
                        borderBottom: '1px solid #ebebeb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }, children: [_jsx(Typography, { variant: "h6", sx: { fontWeight: 600, fontSize: '20px' }, children: group.name }), _jsxs(IconButton, { onClick: () => {
                                if (selectedMemberIds.length > 0) {
                                    handleBulkRemoveMembers();
                                }
                                else {
                                    onDelete(group);
                                }
                            }, disabled: loading ||
                                (members.length > 0 &&
                                    selectedMemberIds.length === 0), sx: {
                                border: '1px solid #da291c',
                                borderRadius: '2px',
                                color: '#da291c',
                                width: 40,
                                height: 40,
                                position: 'relative'
                            }, size: "small", children: [_jsx(DeleteIcon, { fontSize: "small" }), selectedMemberIds.length > 0 && (_jsx(Box, { sx: {
                                        position: 'absolute',
                                        top: -8,
                                        right: -8,
                                        background: '#da291c',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: 20,
                                        height: 20,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        boxShadow: '0 0 0 2px white'
                                    }, children: selectedMemberIds.length }))] })] }), _jsxs(Box, { sx: { flex: 1, p: 3, overflow: 'auto' }, children: [error && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, children: error })), _jsx(Box, { sx: { mb: 3 }, children: _jsx(Autocomplete, { PopperComponent: FullWidthPopper, multiple: true, open: autocompleteOpen, onOpen: () => setAutocompleteOpen(true), onClose: () => setAutocompleteOpen(false), value: selectedUsers, onChange: (event, newValue) => {
                                    setSelectedUsers(newValue);
                                }, options: allUsers.filter((user) => !members.some((m) => m.id === user.id)), getOptionLabel: (option) => {
                                    if (option.firstName || option.lastName) {
                                        return `${option.lastName}${option.lastName && option.firstName
                                            ? ', '
                                            : ''}${option.firstName}`;
                                    }
                                    if (option.name) {
                                        return option.name;
                                    }
                                    return option.email;
                                }, loading: autocompleteLoading, disabled: loading, filterOptions: (options, state) => {
                                    if (!state.inputValue)
                                        return options;
                                    const inputValue = state.inputValue.toLowerCase();
                                    return options.filter((option) => option.firstName
                                        ?.toLowerCase()
                                        .includes(inputValue) ||
                                        option.lastName
                                            ?.toLowerCase()
                                            .includes(inputValue) ||
                                        option.name
                                            ?.toLowerCase()
                                            .includes(inputValue) ||
                                        option.email
                                            ?.toLowerCase()
                                            .includes(inputValue));
                                }, renderInput: (params) => (_jsx(TextField, { ...params, placeholder: selectedUsers.length === 0
                                        ? 'Invite members to this group by name or email'
                                        : '', slotProps: {
                                        ...params.slotProps,
                                        input: {
                                            ...params.slotProps?.input,
                                            startAdornment: (_jsxs(Box, { sx: {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap'
                                                }, children: [_jsx(InputAdornment, { position: "start", sx: { mr: 0.5, ml: 0 }, children: _jsx(SearchIcon, { sx: {
                                                                color: '#212121'
                                                            } }) }), params.slotProps?.input
                                                        ?.startAdornment] })),
                                            endAdornment: (_jsxs(Box, { sx: {
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }, children: [params.slotProps?.input?.endAdornment, selectedUsers.length > 0 && (_jsx(Button, { sx: {
                                                            bgcolor: '#26890d',
                                                            color: 'white',
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '14px',
                                                            letterSpacing: '0.8px',
                                                            px: 2,
                                                            py: 0.75,
                                                            minWidth: 'auto',
                                                            '&:hover': {
                                                                bgcolor: '#1f6f0a'
                                                            }
                                                        }, onClick: handleAddMembers, disabled: loading, children: "Add" }))] }))
                                        }
                                    } })), renderTags: (value, getTagProps) => value.map((option, index) => {
                                    const hasName = option.firstName || option.lastName;
                                    const separator = option.lastName && option.firstName
                                        ? ', '
                                        : '';
                                    const chipLabel = hasName
                                        ? `${option.lastName}${separator}${option.firstName}`
                                        : option.name || option.email;
                                    return (_createElement(Chip, { ...getTagProps({ index }), key: option.id, label: chipLabel, sx: {
                                            bgcolor: 'white',
                                            border: '1px solid #d9d9d9',
                                            height: 28,
                                            m: 0.25,
                                            '& .MuiChip-label': {
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                px: 1
                                            }
                                        } }));
                                }), sx: {
                                    '& .MuiAutocomplete-popupIndicator': {
                                        display: 'none'
                                    },
                                    '& .MuiAutocomplete-clearIndicator': {
                                        display: 'none'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        padding: '8px 12px',
                                        paddingRight: selectedUsers.length > 0
                                            ? '80px'
                                            : '12px',
                                        minHeight: 56,
                                        '& .MuiAutocomplete-input': {
                                            minWidth: 200,
                                            flexGrow: 1,
                                            padding: '4px 0',
                                            height: 'auto',
                                            minHeight: 'unset'
                                        }
                                    },
                                    '& .MuiAutocomplete-endAdornment': {
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)'
                                    }
                                } }) }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 1
                                                    }, children: _jsxs(Stack, { direction: "row", alignItems: "center", gap: 0.5, children: [_jsx(Checkbox, { size: "small", checked: members.length > 0 &&
                                                                    selectedMemberIds.length ===
                                                                        members.length, indeterminate: selectedMemberIds.length >
                                                                    0 &&
                                                                    selectedMemberIds.length <
                                                                        members.length, onChange: (e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedMemberIds(members.map((m) => m.id));
                                                                    }
                                                                    else {
                                                                        setSelectedMemberIds([]);
                                                                    }
                                                                }, sx: {
                                                                    color: '#d9d9d9',
                                                                    '&.Mui-checked': {
                                                                        color: '#26890d'
                                                                    }
                                                                } }), _jsx(Typography, { variant: "body2", sx: {
                                                                    fontWeight: 600,
                                                                    fontSize: '14px'
                                                                }, children: "Team members" }), _jsx(FilterIcon, { sx: {
                                                                    fontSize: 18,
                                                                    color: '#26890d'
                                                                } })] }) }), _jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 1,
                                                        width: 100
                                                    }, children: _jsx(Typography, { variant: "body2", sx: {
                                                            fontWeight: 600,
                                                            fontSize: '14px'
                                                        }, children: "Delete" }) })] }) }), _jsx(TableBody, { children: members.length === 0 ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 2, sx: { textAlign: 'center', py: 4 }, children: _jsx(Typography, { variant: "body1", color: "#888", children: "No members in this group" }) }) })) : (members.map((member, index) => (_jsxs(TableRow, { sx: {
                                                borderTop: index > 0
                                                    ? '1px solid #ebebeb'
                                                    : 'none'
                                            }, children: [_jsx(TableCell, { sx: {
                                                        borderBottom: 'none',
                                                        py: 1
                                                    }, children: _jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, children: [_jsx(Checkbox, { size: "small", checked: selectedMemberIds.includes(member.id), onChange: () => toggleMemberSelection(member.id), sx: {
                                                                    color: '#d9d9d9',
                                                                    '&.Mui-checked': {
                                                                        color: '#26890d'
                                                                    }
                                                                } }), _jsx(Typography, { variant: "body2", sx: {
                                                                    fontSize: '14px'
                                                                }, children: member.firstName ||
                                                                    member.lastName
                                                                    ? `${member.firstName} ${member.lastName}`.trim()
                                                                    : member.name ||
                                                                        member.email })] }) }), _jsx(TableCell, { sx: {
                                                        borderBottom: 'none',
                                                        py: 1
                                                    }, children: _jsx(IconButton, { onClick: () => handleRemoveMember(member.id), disabled: loading, sx: {
                                                            color: '#da291c',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(218, 41, 28, 0.04)'
                                                            }
                                                        }, size: "small", children: _jsx(DeleteIcon, { fontSize: "small" }) }) })] }, member.id)))) })] }) })] }), _jsx(Box, { sx: {
                        p: 2,
                        borderTop: '1px solid #ebebeb',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }, children: _jsx(Button, { variant: "contained", onClick: onClose, sx: {
                            bgcolor: '#26890d',
                            textTransform: 'none',
                            boxShadow: 'none',
                            fontWeight: 600,
                            px: 3,
                            '&:hover': {
                                bgcolor: '#1f6f0a'
                            }
                        }, children: "Close" }) })] }) }));
};
export default UserGroupDrawer;
